<template>
  <div
    ref="linechart"
    class="ct-chart"
    style="height: 300px;"
  />
</template>

<script>
  import Chartist from 'chartist';
  import 'chartist/dist/chartist.min.css';
  import { du, fromUnixTimestamp, n, toUnixTimestamp } from '@/ui/i18n/i18n';
  import { DEFAULT_GRANULARITY } from '@/entities/sensor';

  export default {
    props: {
      chartData: {
        type: Object,
        default: null,
      },
      chartStartsAt: {
        type: Number,
        required: true,
      },
      chartEndsAt: {
        type: Number,
        required: true,
      },
      granularity: {
        type: Number,
        required: false,
        //it cannot be 0, otherwise Chart has infinitive loop in fillGaps
        default: 1,
      },
      showPoint: {
        type: Boolean,
        default: false,
      },
      onlyInteger: {
        type: Boolean,
        default: false,
      },
      fillInTheGaps: {
        type: Boolean,
        default: true,
      },
    },

    data: () => ({
      chartist: null,
      chartTicks: [],
      chartTickPruneFactor: 1,
      chartDisplayHours: false,
    }),

    computed: {
      chartDuration() {
        return this.chartEndsAt - this.chartStartsAt;
      },
      chartResponsiveOptions() {
        return [
          ['screen and (max-width: 992px)', {
            chartPadding: {
              top: 40,
              right: 10,
              bottom: 20,
              left: 0,
            },
            axisX: {
              labelInterpolationFnc: (value, index) => {
                if (index === 0) {
                  return this.formatLabel(value);
                }
                if (index === this.chartTicks.length - 1) {
                  return this.formatLabel(value);
                }
                if (index === parseInt((this.chartTicks.length / 2).toString(), 10)) {
                  return this.formatLabel(value);
                }

                return '';
              },
            },
          }],
        ];
      },

      chartOptions() {
        return {
          lineSmooth: Chartist.Interpolation.none(),
          fullWidth: true,
          showPoint: this.showPoint,
          showLine: true,
          showArea: true,
          chartPadding: {
            top: 40,
            right: 80,
            bottom: 20,
            left: 0,
          },
          axisX: {
            type: Chartist.FixedScaleAxis,
            ticks: this.chartTicks,
            labelInterpolationFnc: (value, index) => {
              if (index % this.chartTickPruneFactor === 0) {
                return this.formatLabel(value);
              }

              return null;
            },
          },
          axisY: {
            offset: 80,
            labelInterpolationFnc: this.formatValue,
            onlyInteger: this.onlyInteger,
          },
        };
      },
    },

    watch: {
      chartData: {
        handler() {
          if (this.chartist) {
            this.destroyChart();
          }
          if (this.chartData) {
            this.drawChart();
          }
        },
        deep: true,
      },
    },

    mounted() {
      if (this.chartData) {
        this.drawChart();
      }
    },

    destroyed() {
      this.destroyChart();
    },

    methods: {
      destroyChart() {
        this.chartTickPruneFactor = 1;
        this.chartDisplayHours = false;
        this.chartTicks = [];

        if (this.chartist) {
          this.chartist.detach();
        }

        if (this.$refs.linechart) {
          while (this.$refs.linechart.firstChild) {
            this.$refs.linechart.removeChild(this.$refs.linechart.firstChild);
          }
        }

        this.chartist = null;
      },

      /**
       * This fills in the gaps for missing measurements
       */
      fillGaps(data) {
        if (!data || !data.series || !data.series[0] || data.series[0].length === 0) {
          return data;
        }

        let position = 0;
        //it cannot be 0, otherwise Chart has infinitive loop in fillGaps
        const step = this.granularity > 0 ? this.granularity : DEFAULT_GRANULARITY;
        const result = [];

        for (let time = this.chartStartsAt; time <= this.chartEndsAt; time += step) {
          if (data.series[0][position] && data.series[0][position].x === time) {
            result.push(data.series[0][position]);
            position += 1;
          } else {
            result.push({
              x: time,
              y: null,
            });
          }
        }

        data.series[0] = result;

        return data;
      },

      drawChart() {
        if (this.chartData.labels && this.chartData.labels.length) {
          const dayLength = 60 * 60 * 24;
          this.chartDisplayHours = this.chartDuration < 2 * dayLength + 10;

          const ticker = new Date(fromUnixTimestamp(this.chartStartsAt));
          ticker.setHours(0, 0, 0);
          let tickerTimestamp = toUnixTimestamp(ticker.getTime());

          // chart is only around a day, give or take a couple leap seconds :)
          if (this.chartDisplayHours) {
            while (tickerTimestamp < this.chartEndsAt) {
              if (tickerTimestamp >= this.chartStartsAt) {
                this.chartTicks.push(tickerTimestamp);
              }
              ticker.setHours(ticker.getHours() + 2);
              tickerTimestamp = toUnixTimestamp(ticker.getTime());
            }
          } else if (this.chartDuration < 2 * 31 * dayLength) {
            while (tickerTimestamp < this.chartEndsAt) {
              if (tickerTimestamp >= this.chartStartsAt) {
                this.chartTicks.push(tickerTimestamp);
              }
              ticker.setDate(ticker.getDate() + 1);
              tickerTimestamp = toUnixTimestamp(ticker.getTime());
            }
          } else {
            ticker.setDate(1);
            tickerTimestamp = toUnixTimestamp(ticker.getTime());
            while (tickerTimestamp < this.chartEndsAt) {
              if (tickerTimestamp >= this.chartStartsAt) {
                this.chartTicks.push(tickerTimestamp);
              }
              ticker.setMonth(ticker.getMonth() + 1);
              tickerTimestamp = toUnixTimestamp(ticker.getTime());
            }
          }
        }

        if (this.chartTicks.length > 20) {
          this.chartTickPruneFactor = parseInt((Math.ceil(this.chartTicks.length / 20)).toString(), 10);
        }

        this.chartist = new Chartist.Line(
          this.$refs.linechart,
          this.fillInTheGaps ? this.fillGaps(this.chartData) : this.chartData,
          this.chartOptions,
          this.chartResponsiveOptions,
        );
      },

      formatValue(value) {
        if (this.chartData.format) {
          return n(value, this.chartData.format);
        }
        return value;
      },

      formatLabel(label) {
        let format = 'shortDateOfYear';
        if (this.chartDisplayHours) {
          format = 'time';
        }
        return du(label, format);
      },
    },
  };
</script>
